import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
} from "react";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import Card from "../common/Card";
import moment from "moment";
import {
  Scale,
  LocalDrink,
  DirectionsWalk,
  Restaurant,
} from "@mui/icons-material";

const Container = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  flex-wrap: wrap;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${(props) =>
    props.theme?.isDark &&
    `
    text-shadow: 0 0 5px rgba(25, 184, 136, 0.3);
  `}
`;

const PeriodSelector = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const PeriodButton = styled.button`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm}
    ${(props) => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background: ${(props) =>
    props.$active
      ? getTheme(props.theme?.isDark).colors.primary
      : getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    props.$active
      ? "#ffffff"
      : getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$active
        ? getTheme(props.theme?.isDark).colors.primary
        : getTheme(props.theme?.isDark).colors.hover};
  }
`;

const DateRangeDisplay = styled.div`
  font-size: 14px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  margin-left: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.xs}
    ${(props) => getTheme(props.theme?.isDark).spacing.md};
  background: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(350px, 1fr)
  );
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};

  @media (max-width: 1200px) and (min-width: 769px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${(props) =>
      getTheme(props.theme?.isDark).spacing.md};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${(props) =>
      getTheme(props.theme?.isDark).spacing.md};
  }
`;

const ChartCard = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  min-width: 0;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  flex-wrap: wrap;
`;

const ChartIcon = styled.div`
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
  display: flex;
  align-items: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const ChartTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

const ChartContainer = styled.div`
  width: 100%;
  min-height: 250px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm}
    0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  font-style: italic;
  grid-column: 1 / -1;
`;

// Simple Line Chart Component
const SimpleLineChart = ({
  data,
  color,
  theme,
  label,
  goal,
  containerRef,
}) => {
  const [chartWidth, setChartWidth] = useState(300);

  useEffect(() => {
    if (containerRef?.current) {
      const updateWidth = () => {
        const containerWidth =
          containerRef.current.offsetWidth || 300;
        const calculatedWidth = Math.max(
          containerWidth - 40,
          300
        );
        setChartWidth(calculatedWidth);
      };

      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () =>
        window.removeEventListener("resize", updateWidth);
    }
  }, [containerRef]);

  if (!data || data.length === 0) {
    return (
      <EmptyState theme={theme}>Məlumat yoxdur</EmptyState>
    );
  }

  const containerHeight = 200;
  const width = chartWidth;
  const height = containerHeight;
  const padding = 20;
  const chartWidthInner = width - padding * 2;
  const chartHeight = height - padding * 2;

  const maxValue = Math.max(
    ...data.map((d) => d.value),
    goal || 1
  );
  const minValue = Math.min(
    ...data.map((d) => d.value),
    goal || 0
  );

  const points = data.map((d, index) => {
    const x =
      padding +
      (index / (data.length - 1 || 1)) * chartWidthInner;
    const y =
      padding +
      chartHeight -
      ((d.value - minValue) / (maxValue - minValue || 1)) *
        chartHeight;
    return { x, y, value: d.value, date: d.date };
  });

  const pathData = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  // Goal line
  let goalY = null;
  if (goal && goal >= minValue && goal <= maxValue) {
    goalY =
      padding +
      chartHeight -
      ((goal - minValue) / (maxValue - minValue || 1)) *
        chartHeight;
  }

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ overflow: "visible" }}
    >
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
        const y = padding + chartHeight * (1 - ratio);
        const value =
          minValue + (maxValue - minValue) * ratio;
        return (
          <g key={ratio}>
            <line
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke={getTheme(theme?.isDark).colors.border}
              strokeWidth="1"
              strokeDasharray="2,2"
            />
            <text
              x={padding - 5}
              y={y + 4}
              fontSize="10"
              fill={
                getTheme(theme?.isDark).colors.textSecondary
              }
              textAnchor="end"
            >
              {Math.round(value)}
            </text>
          </g>
        );
      })}

      {/* Chart line */}
      <path
        d={pathData}
        fill="none"
        stroke={color}
        strokeWidth="2"
      />

      {/* Data points */}
      {points.map((point, index) => (
        <g key={index}>
          <circle
            cx={point.x}
            cy={point.y}
            r="4"
            fill={color}
            stroke="#fff"
            strokeWidth="2"
          />
          <title>{`${point.date}: ${point.value}${
            goal ? ` (Hədəf: ${goal})` : ""
          }`}</title>
        </g>
      ))}

      {/* Goal line - rendered after data points to appear on top */}
      {goalY !== null && (
        <g>
          <line
            x1={padding}
            y1={goalY}
            x2={width - padding}
            y2={goalY}
            stroke={
              getTheme(theme?.isDark).colors.warning ||
              "#FF8C39"
            }
            strokeWidth="2.5"
            strokeDasharray="5,5"
            opacity="1"
          />
          <text
            x={width - padding - 5}
            y={goalY - 5}
            fontSize="11"
            fill={
              getTheme(theme?.isDark).colors.warning ||
              "#FF8C39"
            }
            fontWeight="700"
            textAnchor="end"
            style={{ pointerEvents: "none" }}
          >
            🎯 {goal}
          </text>
        </g>
      )}
    </svg>
  );
};

// Simple Bar Chart Component
const SimpleBarChart = ({
  data,
  color,
  theme,
  label,
  goal,
  containerRef,
}) => {
  const [chartWidth, setChartWidth] = useState(300);

  useEffect(() => {
    if (containerRef?.current) {
      const updateWidth = () => {
        const containerWidth =
          containerRef.current.offsetWidth || 300;
        const calculatedWidth = Math.max(
          containerWidth - 40,
          300
        );
        setChartWidth(calculatedWidth);
      };

      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () =>
        window.removeEventListener("resize", updateWidth);
    }
  }, [containerRef]);

  if (!data || data.length === 0) {
    return (
      <EmptyState theme={theme}>Məlumat yoxdur</EmptyState>
    );
  }

  const containerHeight = 200;
  const width = chartWidth;
  const height = containerHeight;
  const padding = 20;
  const chartWidthInner = width - padding * 2;
  const chartHeight = height - padding * 2;
  const barWidth =
    data.length > 0
      ? Math.max(chartWidthInner / data.length - 4, 10)
      : 20;

  const maxValue = Math.max(
    ...data.map((d) => d.value),
    goal || 1
  );

  // Goal line
  let goalY = null;
  if (goal && goal > 0) {
    goalY =
      padding +
      chartHeight -
      (goal / maxValue) * chartHeight;
  }

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ overflow: "visible" }}
    >
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
        const y = padding + chartHeight * (1 - ratio);
        const value = maxValue * ratio;
        return (
          <g key={ratio}>
            <line
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke={getTheme(theme?.isDark).colors.border}
              strokeWidth="1"
              strokeDasharray="2,2"
            />
            <text
              x={padding - 5}
              y={y + 4}
              fontSize="10"
              fill={
                getTheme(theme?.isDark).colors.textSecondary
              }
              textAnchor="end"
            >
              {Math.round(value)}
            </text>
          </g>
        );
      })}

      {/* Bars */}
      {data.map((d, index) => {
        const barHeight =
          (d.value / maxValue) * chartHeight;
        const x =
          padding +
          index * (chartWidthInner / data.length) +
          2;
        const y = padding + chartHeight - barHeight;
        const isAboveGoal = goal && d.value >= goal;
        return (
          <g key={index}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill={
                isAboveGoal
                  ? getTheme(theme?.isDark).colors
                      .success || "#19B888"
                  : color
              }
              opacity="0.8"
            />
            <title>{`${d.date}: ${d.value}${
              goal ? ` (Hədəf: ${goal})` : ""
            }`}</title>
          </g>
        );
      })}

      {/* Goal line - rendered after bars to appear on top */}
      {goalY !== null && (
        <g>
          <line
            x1={padding}
            y1={goalY}
            x2={width - padding}
            y2={goalY}
            stroke={
              getTheme(theme?.isDark).colors.warning ||
              "#FF8C39"
            }
            strokeWidth="2.5"
            strokeDasharray="5,5"
            opacity="1"
          />
          <text
            x={width - padding - 5}
            y={goalY - 5}
            fontSize="11"
            fill={
              getTheme(theme?.isDark).colors.warning ||
              "#FF8C39"
            }
            fontWeight="700"
            textAnchor="end"
            style={{ pointerEvents: "none" }}
          >
            🎯 {goal}
          </text>
        </g>
      )}
    </svg>
  );
};

// Helper function to calculate water goal
const calculateWaterGoal = (weight) => {
  if (!weight) return 0;
  // Base: weight * 35ml per kg
  return Math.round(weight * 35);
};

// Helper function to calculate daily calorie goal
const calculateDailyCalorieGoal = ({
  weight,
  height,
  gender,
  activityLevel,
  birthdate,
  weightTo,
}) => {
  if (
    !weight ||
    !height ||
    !gender ||
    !activityLevel ||
    !birthdate
  )
    return 0;

  const age = moment().diff(birthdate, "years");
  let bmr = 0;

  if (gender === "male") {
    bmr = 10 * weight + 6.25 * height - 5 * age + 5;
  } else {
    bmr = 10 * weight + 6.25 * height - 5 * age - 161;
  }

  const tdee = bmr * activityLevel;

  // Determine goal type
  let goalType = "maintain";
  if (weightTo) {
    if (weight > weightTo) goalType = "lose";
    else if (weight < weightTo) goalType = "gain";
  }

  let dailyCalories = tdee;
  if (goalType === "lose") {
    dailyCalories = tdee - 300;
  } else if (goalType === "gain") {
    dailyCalories = tdee + 300;
  }

  return Math.round(Math.max(dailyCalories, 1200));
};

const DateInput = styled.input`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm}
    ${(props) => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }
`;

const CustomerLogsAnalytics = ({ logs, customer }) => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const [period, setPeriod] = useState("daily"); // daily, weekly, monthly
  const [startDate, setStartDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const weightChartRef = useRef(null);
  const waterChartRef = useRef(null);
  const walkingChartRef = useRef(null);
  const caloriesChartRef = useRef(null);

  // Calculate goals
  const goals = useMemo(() => {
    if (!customer) return null;

    return {
      weight: customer.weightTo || null,
      water: calculateWaterGoal(
        customer.weight || customer.currentWeight
      ),
      walking: 10000, // Standard daily walking goal
      calories: calculateDailyCalorieGoal({
        weight: customer.weight || customer.currentWeight,
        height: customer.height,
        gender: customer.gender,
        activityLevel: customer.activityLevel || 1.5,
        birthdate: customer.birthdate,
        weightTo: customer.weightTo,
      }),
    };
  }, [customer]);

  const dateRange = useMemo(() => {
    const selectedMoment = moment(startDate);

    switch (period) {
      case "weekly":
        return {
          start: selectedMoment
            .clone()
            .subtract(6, "days")
            .startOf("day")
            .toDate(),
          end: selectedMoment.endOf("day").toDate(),
        };
      case "monthly":
        return {
          start: selectedMoment
            .clone()
            .subtract(29, "days")
            .startOf("day")
            .toDate(),
          end: selectedMoment.endOf("day").toDate(),
        };
      default: // daily
        return {
          start: selectedMoment.startOf("day").toDate(),
          end: selectedMoment.endOf("day").toDate(),
        };
    }
  }, [period, startDate]);

  const chartData = useMemo(() => {
    if (!logs) return null;

    const { start, end } = dateRange;
    const startMoment = moment(start);
    const endMoment = moment(end);

    // Generate date array based on period
    const dates = [];
    let current = startMoment.clone();
    const daysToShow =
      period === "daily" ? 1 : period === "weekly" ? 7 : 30;

    for (let i = 0; i < daysToShow; i++) {
      dates.push(current.clone());
      current.add(1, "day");
    }

    // Weight data - collect all weight points including initial weight
    const weightPoints = [];

    // Add initial weight as the starting point at the beginning of the date range
    if (customer?.weight) {
      // Always use the start of the selected date range as the initial weight point date
      weightPoints.push({
        date: startMoment.format("DD.MM"),
        dateMoment: startMoment.clone(),
        value: customer.weight,
      });
    }

    // Add all weight logs within the date range
    if (logs.weightLogs) {
      logs.weightLogs.forEach((log) => {
        const logDate = moment(log.createdAt);
        if (
          logDate.isSameOrAfter(startMoment, "day") &&
          logDate.isSameOrBefore(endMoment, "day")
        ) {
          weightPoints.push({
            date: logDate.format("DD.MM"),
            dateMoment: logDate,
            value: log.weight,
          });
        }
      });
    }

    // Sort by date and remove duplicates (keep latest value for each date)
    // Weight logs will naturally override initial weight if they're on the same date
    weightPoints.sort(
      (a, b) =>
        a.dateMoment.valueOf() - b.dateMoment.valueOf()
    );
    const weightDataMap = new Map();
    weightPoints.forEach((point) => {
      const dateKey = point.dateMoment.format("YYYY-MM-DD");
      // Keep the latest value for each date (logs added after initial weight will override it)
      if (
        !weightDataMap.has(dateKey) ||
        point.dateMoment.isAfter(
          weightDataMap.get(dateKey).dateMoment
        )
      ) {
        weightDataMap.set(dateKey, point);
      }
    });

    // Convert back to array, sort by date, and remove dateMoment
    const weightData = Array.from(weightDataMap.values())
      .sort(
        (a, b) =>
          a.dateMoment.valueOf() - b.dateMoment.valueOf()
      )
      .map((point) => ({
        date: point.date,
        value: point.value,
      }));

    // Water data
    const waterData = dates.map((date) => {
      const dayLogs =
        logs.waterLogs?.filter((log) => {
          const logDate = moment(log.createdAt);
          return logDate.isSame(date, "day");
        }) || [];
      const total = dayLogs.reduce(
        (sum, log) => sum + (log.quantity || 0),
        0
      );
      return {
        date: date.format("DD.MM"),
        value: total,
      };
    });

    // Walking data
    const walkingData = dates.map((date) => {
      const dayLogs =
        logs.walkingLogs?.filter((log) => {
          const logDate = moment(log.date || log.createdAt);
          return logDate.isSame(date, "day");
        }) || [];
      const total = dayLogs.reduce(
        (sum, log) => sum + (log.count || 0),
        0
      );
      return {
        date: date.format("DD.MM"),
        value: total,
      };
    });

    // Calories data
    const caloriesData = dates.map((date) => {
      const dayLogs =
        logs.mealLogs?.filter((log) => {
          const logDate = moment(log.date || log.createdAt);
          return logDate.isSame(date, "day");
        }) || [];
      const total = dayLogs.reduce((sum, log) => {
        // Check if food and nutrition exist
        if (log.food?.nutrition?.calories) {
          const nutrition = log.food.nutrition;
          const baseUnit =
            nutrition.base_unit || "per_100g";

          // Determine unit value based on base_unit
          let unitValue = 100; // default for per_100g and per_100ml
          if (
            baseUnit === "per_serving" ||
            baseUnit === "per_piece" ||
            baseUnit === "per_cup"
          ) {
            unitValue = 1;
          }

          // Calculate calories: (nutrition.calories * quantity) / unitValue
          const calories =
            (nutrition.calories * log.quantity) / unitValue;
          return sum + calories;
        }
        return sum;
      }, 0);
      return {
        date: date.format("DD.MM"),
        value: Math.round(total),
      };
    });

    return {
      weight: weightData.filter((d) => d.value > 0),
      water: waterData,
      walking: walkingData,
      calories: caloriesData,
    };
  }, [logs, dateRange, period, customer]);

  const totals = useMemo(() => {
    if (!chartData) return null;

    return {
      weight:
        chartData.weight.length > 0
          ? chartData.weight[chartData.weight.length - 1]
              .value
          : null,
      water: chartData.water.reduce(
        (sum, d) => sum + d.value,
        0
      ),
      walking: chartData.walking.reduce(
        (sum, d) => sum + d.value,
        0
      ),
      calories: chartData.calories.reduce(
        (sum, d) => sum + d.value,
        0
      ),
    };
  }, [chartData]);

  const formatDateRange = () => {
    const { start, end } = dateRange;
    const startMoment = moment(start);
    const endMoment = moment(end);

    if (period === "daily") {
      return startMoment.format("DD.MM.YYYY");
    } else if (period === "weekly") {
      return `${startMoment.format(
        "DD.MM"
      )} - ${endMoment.format("DD.MM.YYYY")}`;
    } else {
      return `${startMoment.format(
        "DD.MM"
      )} - ${endMoment.format("DD.MM.YYYY")}`;
    }
  };

  if (!logs) return null;

  const primaryColor = getTheme(isDark).colors.primary;

  return (
    <Container theme={theme}>
      <Header>
        <Title theme={theme}>Log Analizi</Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: getTheme(isDark).spacing.md,
            flexWrap: "wrap",
          }}
        >
          <PeriodSelector>
            <PeriodButton
              $active={period === "daily"}
              onClick={() => setPeriod("daily")}
              theme={theme}
            >
              Gündəlik
            </PeriodButton>
            <PeriodButton
              $active={period === "weekly"}
              onClick={() => setPeriod("weekly")}
              theme={theme}
            >
              Həftəlik
            </PeriodButton>
            <PeriodButton
              $active={period === "monthly"}
              onClick={() => setPeriod("monthly")}
              theme={theme}
            >
              Aylıq
            </PeriodButton>
          </PeriodSelector>
          <DateInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={moment().format("YYYY-MM-DD")}
            theme={theme}
          />
          <DateRangeDisplay theme={theme}>
            {formatDateRange()}
          </DateRangeDisplay>
        </div>
      </Header>
      <ChartsGrid theme={theme}>
        {chartData && (
          <>
            {chartData.weight.length > 0 && (
              <ChartCard theme={theme}>
                <ChartHeader>
                  <ChartIcon theme={theme}>
                    <Scale />
                  </ChartIcon>
                  <ChartTitle theme={theme}>
                    Çəki{" "}
                    {totals.weight &&
                      `(${totals.weight} kq)`}{" "}
                    {goals?.weight &&
                      `🎯 ${goals.weight} kq`}
                  </ChartTitle>
                </ChartHeader>
                <ChartContainer ref={weightChartRef}>
                  <SimpleLineChart
                    data={chartData.weight}
                    color={primaryColor}
                    theme={theme}
                    label="Çəki (kq)"
                    goal={goals?.weight}
                    containerRef={weightChartRef}
                  />
                </ChartContainer>
              </ChartCard>
            )}
            <ChartCard theme={theme}>
              <ChartHeader>
                <ChartIcon theme={theme}>
                  <LocalDrink />
                </ChartIcon>
                <ChartTitle theme={theme}>
                  Su{" "}
                  {totals.water > 0 &&
                    `(${totals.water} ml)`}{" "}
                  {goals?.water && `🎯 ${goals.water} ml`}
                </ChartTitle>
              </ChartHeader>
              <ChartContainer ref={waterChartRef}>
                <SimpleBarChart
                  data={chartData.water}
                  color={primaryColor}
                  theme={theme}
                  label="Su (ml)"
                  goal={goals?.water}
                  containerRef={waterChartRef}
                />
              </ChartContainer>
            </ChartCard>
            <ChartCard theme={theme}>
              <ChartHeader>
                <ChartIcon theme={theme}>
                  <DirectionsWalk />
                </ChartIcon>
                <ChartTitle theme={theme}>
                  Yürüyüş{" "}
                  {totals.walking > 0 &&
                    `(${totals.walking} addım)`}{" "}
                  {goals?.walking &&
                    `🎯 ${goals.walking} addım`}
                </ChartTitle>
              </ChartHeader>
              <ChartContainer ref={walkingChartRef}>
                <SimpleBarChart
                  data={chartData.walking}
                  color={primaryColor}
                  theme={theme}
                  label="Yürüyüş (addım)"
                  goal={goals?.walking}
                  containerRef={walkingChartRef}
                />
              </ChartContainer>
            </ChartCard>
            <ChartCard theme={theme}>
              <ChartHeader>
                <ChartIcon theme={theme}>
                  <Restaurant />
                </ChartIcon>
                <ChartTitle theme={theme}>
                  Kalori{" "}
                  {totals.calories > 0 &&
                    `(${totals.calories} kcal)`}{" "}
                  {goals?.calories &&
                    `🎯 ${goals.calories} kcal`}
                </ChartTitle>
              </ChartHeader>
              <ChartContainer ref={caloriesChartRef}>
                <SimpleBarChart
                  data={chartData.calories}
                  color={primaryColor}
                  theme={theme}
                  label="Kalori (kcal)"
                  goal={goals?.calories}
                  containerRef={caloriesChartRef}
                />
              </ChartContainer>
            </ChartCard>
          </>
        )}
      </ChartsGrid>
    </Container>
  );
};

export default CustomerLogsAnalytics;
